import { rhybusRListsHttp } from '../api/rhybus-list-http'
import { rhybusWhoHttp } from '../api/rhybus-who-http'
import { ListNames, DETAIL_PROGRESS_FLOW, DETAIL_PROGRESS_TEMPLATE, DetailProgressStep, initialDiscussionSuggestions, getUserByUserId, STATUS_CODE_TRAINIG } from '../config/configuration'
import { ACTIVITY_ID, sendNotification, triggerCaseNotifications } from './notificationUtils'
import { formatCurrency } from '@rhybus/common-functions'
import type { IBlobInfo } from '@rhybus/api'
import type { I_History } from '../interfaces/master.interface'

const formatProgressDate = (date: string): string => {
	if (!date) return ''
	const d = new Date(date)
	const mm = String(d.getMonth() + 1).padStart(2, '0')
	const dd = String(d.getDate()).padStart(2, '0')
	const yyyy = d.getFullYear()
	const timePart = d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true })
	return `${mm}/${dd}/${yyyy} ${timePart}`
}


const formatBlobDate = (date: string): string => {
	if (!date) return ''
	const d = new Date(date)
	const datePart = d.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })
	const timePart = d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true })
	return `${datePart} ${timePart}`
}

const normalizeProgressKey = (value?: string) =>
	(value ?? '').toString().trim().toUpperCase().replace(/[\s/-]+/g, '_')

const getHistoryUser = (item: I_History): string => {
	return item?.ItemCreatedBy
		|| item?.ItemModifiedBy
		|| 'System'
}

const getHistoryDate = (item: I_History): string => {
	return formatProgressDate((item?.ItemCreated || item?.ItemModified || '') as string)
}

const getProgressIndexFromHistory = (item: I_History, flow: { code: string; label: string }[]): number => {
	const normalizedStatusCode = normalizeProgressKey(item?.StatusCode)
	const normalizedStatusTitle = normalizeProgressKey(item?.StatusTitle)
	return flow.findIndex((step) => (
		step.code === normalizedStatusCode
		|| normalizeProgressKey(step.label) === normalizedStatusTitle
	))
}

export const buildDetailProgressSteps = (
	historyItems: I_History[] = [],
	currentItem?: any | null,
	template: DetailProgressStep[] = DETAIL_PROGRESS_TEMPLATE,
	flow: { code: string; label: string }[] = DETAIL_PROGRESS_FLOW
): DetailProgressStep[] => {
	const sortedHistory = [...historyItems].sort((a, b) => {
		const aDate = new Date((a?.ItemCreated || a?.ItemModified || '') as string).valueOf()
		const bDate = new Date((b?.ItemCreated || b?.ItemModified || '') as string).valueOf()
		return aDate - bDate
	})

	const steps = template.map(step => ({
		...step,
		statusType: '',
		history: [] as DetailProgressStep['history'],
	}))

	const stepHistoryItems = sortedHistory.map((item, index) => ({
		item,
		index,
		progressIndex: getProgressIndexFromHistory(item, flow),
		user: getHistoryUser(item),
		dateTime: getHistoryDate(item),
		label: item?.Action || item?.StatusTitle || item?.PreviousStatusTitle || '',
	}))

	// Initiated step always uses the case item's own submission data, not history records
	const submittedUser = currentItem?.submittedBy
		|| (typeof currentItem?.ItemCreatedBy === 'string' ? currentItem.ItemCreatedBy : '')
		|| currentItem?.ItemCreatedBy?.Title
		|| currentItem?.ItemCreatedBy?.Name
		|| ''
	const submittedDate = currentItem?.ItemCreated
		? formatProgressDate(currentItem.ItemCreated as string)
		: (currentItem?.submitteddate || '')

	steps[0].statusType = 'completed'
	if (submittedUser || submittedDate) {
		steps[0].history.push({
			user: submittedUser || 'System',
			dateTime: submittedDate,
			statusLabel: 'Submitted',
		})
	}

	let activeStepIndex = stepHistoryItems.reduce((maxIndex, record) => (
		record.progressIndex > maxIndex ? record.progressIndex : maxIndex
	), -1)

	const currentStatusIndex = currentItem ? flow.findIndex((step) => (
		step.code === normalizeProgressKey(currentItem?.StatusCode)
		|| normalizeProgressKey(step.label) === normalizeProgressKey(currentItem?.StatusTitle)
	)) : -1

	if (currentStatusIndex >= 0) {
		activeStepIndex = Math.max(activeStepIndex, currentStatusIndex)
	}

	stepHistoryItems.forEach((record) => {
		// progressIndex < 1: unmatched (<0) or StatusCode=4 "entered Internal Review" (=0) — skip both;
		// Initiated is already populated from the case item. We want each step to show who COMPLETED it,
		// which is the approval that moved the case OUT of that step (i.e. one flow index ahead).
		if (record.progressIndex < 1) {
			return
		}

		const targetStep = steps[record.progressIndex]
		if (targetStep) {
			targetStep.history.push({
				user: record.user,
				dateTime: record.dateTime,
				statusLabel: record.label || flow[record.progressIndex]?.label || '',
			})
		}
	})

	steps.forEach((step, index) => {
		if (index === 0) {
			return
		}

		if (activeStepIndex < 0) {
			step.statusType = ''
			return
		}

		if (index - 1 < activeStepIndex) {
			step.statusType = 'completed'
		} else if (index - 1 === activeStepIndex) {
			step.statusType = 'active'
		}
	})

	// Suppress popup for active (orange) steps — only completed (green) steps show user/date
	steps.forEach((step) => {
		if (step.statusType === 'active') {
			step.history = []
		}
	})

	// Handle cancelled status
	if (currentItem?.StatusCode && normalizeProgressKey(currentItem.StatusCode) === '3') {
		// Case is cancelled - find the cancel record in history
		const cancelRecord = stepHistoryItems.find((record) =>
			normalizeProgressKey(record.label) === 'CANCEL' ||
			normalizeProgressKey(record.item?.Action) === 'CANCEL'
		)

		if (cancelRecord) {
			// Mark all steps up to activeStepIndex as completed
			steps.forEach((step, index) => {
				if (index === 0) return
				if (index - 1 < activeStepIndex) {
					step.statusType = 'completed'
				} else if (index - 1 === activeStepIndex) {
					// Mark current step as cancelled with cancel details
					step.statusType = 'cancelled'
					step.history.push({
						user: cancelRecord.user,
						dateTime: cancelRecord.dateTime,
						statusLabel: 'Cancelled',
					})
				}
			})
		}
	} else if (currentItem?.StatusCode && normalizeProgressKey(currentItem.StatusCode) === 'COMPLETED') {
		steps.forEach(step => {
			step.statusType = 'completed'
		})
	}

	return steps
}

export const detailProgressSteps = DETAIL_PROGRESS_TEMPLATE

export const createDiscussion = (discussionData: any, itemGuid: string) => ({
	ID: Date.now(),
	Subject: discussionData.Subject,
	Comment: discussionData.Comment,
	CommentType: discussionData.CommentType || 'Private',
	DocumentName: discussionData.DocumentName || '',
	IsActionComment: false,
	ItemGUID: itemGuid,
	ItemCreated: new Date().toISOString(),
	ItemCreatedBy: {
		Title: 'Current User',
		EMail: 'current.user@example.com',
		ID: 1,
	},
	CommentTo: discussionData.CommentTo,
	replies: [],
})

export const createReply = (replyData: any, itemGuid: string) => ({
	ID: Date.now(),
	ParentCommentID: replyData.ParentCommentID?.toString(),
	Comment: replyData.Comment,
	DocumentName: replyData.DocumentName || '',
	IsActionComment: false,
	ItemGUID: itemGuid,
	ItemCreated: new Date().toISOString(),
	ItemCreatedBy: {
		Title: 'Current User',
		EMail: 'current.user@example.com',
		ID: 1,
	},
})

export const addReplyToDiscussions = (discussions: any[], reply: any) => discussions.map((discussion) => (
	discussion.ID.toString() === reply.ParentCommentID
		? { ...discussion, replies: [...(discussion.replies || []), reply] }
		: discussion
))

export const filterDiscussionSuggestions = (value: string) => initialDiscussionSuggestions.filter((suggestion) => (
	suggestion.DisplayText.toLowerCase().includes(value.toLowerCase())
	|| suggestion.email.toLowerCase().includes(value.toLowerCase())
))

export const filterTeamPeople = (value: string, teamData: any[]) => teamData.filter((person: any) => (
	person.Title.toLowerCase().includes(value.toLowerCase())
	|| person.EMail.toLowerCase().includes(value.toLowerCase())
))

export const addTeamMemberToList = (teamMembers: any[], userData: any, inputValues?: any) => {
	const nextMember = inputValues || userData
	if (!nextMember?.EMail) {
		return teamMembers
	}

	const alreadyExists = teamMembers.some((member) => member.EMail === nextMember.EMail)
	if (alreadyExists) {
		return teamMembers
	}

	return [
		...teamMembers,
		{
			ID: nextMember.ID || Date.now(),
			Title: nextMember.Title,
			EMail: nextMember.EMail,
		},
	]
}

export const removeTeamMembersBySelection = (teamMembers: any[], selectedMembers: any[]) => {
	const selectedEmails = new Set(selectedMembers.map((member) => member.EMail))
	return teamMembers.filter((member) => !selectedEmails.has(member.EMail))
}

export const validateTeamMember = (teamMembers: any[], userData: any) => {
	if (!userData?.EMail) {
		return { isValid: false, error: 'Please enter Team Member' }
	}

	const alreadyExists = teamMembers.some((member) => member.EMail === userData.EMail)
	if (alreadyExists) {
		return { isValid: false, error: 'Team member already exists' }
	}

	return { isValid: true, error: '' }
}

// ============================================
// FILE MANAGEMENT FUNCTIONS FOR RhyBusFiles
// ============================================

const EMPTY_BLOB_ETAG = ''
const FOLDER_PLACEHOLDER_FILE = '.folder'
const ROOT_BLOB_FOLDER = '/'

const normalizeBlobFolder = (folderName?: string) => (folderName ?? '').trim()

// Converts a folder name to the /path format required by the RhyBus blob API.
// undefined/'' → '/'  |  'docs' → '/docs'  |  '/docs' → '/docs'
const toBlobFolderPath = (folderName?: string): string => {
	const trimmed = normalizeBlobFolder(folderName)
	if (!trimmed || trimmed === '/') return '/'
	return trimmed.startsWith('/') ? trimmed : `/${trimmed}`
}

const isFolderPlaceholder = (blob: Partial<IBlobInfo>) => blob?.fileName === FOLDER_PLACEHOLDER_FILE

const resolveUserDisplayNames = async (userIds: string[]): Promise<Map<string, string>> => {
	const uniqueIds = [...new Set(userIds.filter(Boolean))]
	if (!uniqueIds.length) return new Map()
	const whoHttp = rhybusWhoHttp()
	const entries = await Promise.all(
		uniqueIds.map(async (id) => {
			try {
				const user = await whoHttp.OtherByUserId(id)
				return [id, user?.profileName ?? id] as [string, string]
			} catch {
				return [id, id] as [string, string]
			}
		})
	)
	return new Map(entries)
}

const mapBlobToRhyBusFile = (blob: IBlobInfo, userNames: Map<string, string> = new Map()) => {
	const metadata = blob.metadata as any
	const metadataObject = metadata instanceof Map ? Object.fromEntries(metadata.entries()) : metadata ?? {}
	const name = blob.fileName
	const createdByName = blob.createdBy ? (userNames.get(blob.createdBy) ?? blob.createdBy) : ''
	const modifiedByName = blob.modifiedBy ? (userNames.get(blob.modifiedBy) ?? blob.modifiedBy) : createdByName

	return {
		UniqueId: blob.eKey ?? `${blob.folderName || ROOT_BLOB_FOLDER}-${name}`,
		ID: blob.eKey ?? `${blob.folderName || ROOT_BLOB_FOLDER}-${name}`,
		Id: blob.eKey ?? `${blob.folderName || ROOT_BLOB_FOLDER}-${name}`,
		Name: name,
		Title: name,
		ServerRelativeUrl: '',
		eKey: blob.eKey,
		eTag: blob.eTag,
		folderName: blob.folderName ?? ROOT_BLOB_FOLDER,
		metadata: metadataObject,
		CreatedBy: createdByName,
		Created: blob.modifiedOn? formatBlobDate(String(blob.modifiedOn)): blob.createdOn? formatBlobDate(String(blob.createdOn)): '',
		Author: { Title: createdByName || 'System' },
		TimeCreated: blob.createdOn,
		ItemCreated: blob.createdOn,
		ItemCreatedBy: { Title: createdByName || 'System' },
		ItemModified: blob.modifiedOn,
		ItemModifiedBy: { Title: modifiedByName || 'System' },
	}
}

const getCaseMasterBlobStore = async () => {
	const list = await rhybusRListsHttp().getList(ListNames.DSCA_CASE_MASTER)
	return list.blobs()
}

const getTrainingTrackBlobStore = async () => {
	const list = await rhybusRListsHttp().getList(ListNames.DSCA_TRAINING_TRACK)
	return list.blobs()
}

const getAnnouncementBlobStore = async () => {
	const list = await rhybusRListsHttp().getList(ListNames.ANNOUNCEMENT)
	return list.blobs()
}

const getKnowledgeGraphBlobStore = async () => {
	const list = await rhybusRListsHttp().getList(ListNames.KNOWLEDGEGRAPH)
	return list.blobs()
}

const getQaBlobStore = async () => {
	const list = await rhybusRListsHttp().getList(ListNames.QANDA)
	return list.blobs()
}

const getCalendarBlobStore = async () => {
	const list = await rhybusRListsHttp().getList(ListNames.CALENDAR)
	return list.blobs()
}

const getFeedbackBlobStore = async () => {
	const list = await rhybusRListsHttp().getList(ListNames.FEEDBACK)
	return list.blobs()
}

const getBlobStore = (pageName?: string) => {
	switch (pageName) {
		case 'training-detail-view': return getTrainingTrackBlobStore()
		case 'announcements': return getAnnouncementBlobStore()
		case 'knowledge-base': return getKnowledgeGraphBlobStore()
		case 'qanda': return getQaBlobStore()
		case 'eventcalendar': return getCalendarBlobStore()
		case 'sitefeedback': return getFeedbackBlobStore()
		default: return getCaseMasterBlobStore()
	}
}

export const getFeedbackBlobAttachments = async (itemKey: string): Promise<{ FileName: string; Name: string; eKey?: string }[]> => {
	if (!itemKey) return []
	const blobStore = await getFeedbackBlobStore()
	const blobs: IBlobInfo[] = await blobStore.list(itemKey, ROOT_BLOB_FOLDER) ?? []
	return blobs
		.filter((b) => !isFolderPlaceholder(b))
		.filter((b) => {
			const meta = b.metadata as any
			const metaObj: Record<string, string> = meta instanceof Map ? Object.fromEntries(meta.entries()) : meta ?? {}
			return metaObj.IsDeleted !== 'true'
		})
		.map((b) => ({ FileName: b.fileName ?? '', Name: b.fileName ?? '', eKey: b.eKey }))
}

export const structureCaseBlobsForRhyBusFiles = async (itemKey: string, pageName?: string) => {
	if (!itemKey) {
		return createEmptyFileStructure()
	}

	const blobStore = await getBlobStore(pageName)
	const [rootBlobs, folderNames] = await Promise.all([
		blobStore.list(itemKey, ROOT_BLOB_FOLDER),
		blobStore.listFolders(itemKey),
	])

	const validFolderNames = (folderNames ?? []).filter(Boolean).filter((n: string) => n !== '/')

	const folderBlobsMap = new Map<string, IBlobInfo[]>()
	await Promise.all(
		validFolderNames.map(async (folderName: string) => {
			const blobs = await blobStore.list(itemKey, toBlobFolderPath(folderName))
			folderBlobsMap.set(folderName, blobs ?? [])
		})
	)

	const allBlobs: IBlobInfo[] = [
		...(rootBlobs ?? []),
		...Array.from(folderBlobsMap.values()).flat(),
	]
	const userIds = allBlobs.map((b) => b.createdBy ?? '').filter(Boolean)
	const userNames = await resolveUserDisplayNames(userIds)

	// Exclude folder placeholders and any blob whose IsDeleted metadata flag is 'true'.
	const isVisible = (blob: IBlobInfo): boolean => {
		if (isFolderPlaceholder(blob)) return false
		const meta = blob.metadata as any
		const metaObj: Record<string, string> = meta instanceof Map ? Object.fromEntries(meta.entries()) : meta ?? {}
		return metaObj.IsDeleted !== 'true'
	}

	const files = (rootBlobs ?? [])
		.filter(isVisible)
		.map((blob: IBlobInfo) => mapBlobToRhyBusFile(blob, userNames))
		.sort((a: any, b: any) => a?.Name?.localeCompare(b?.Name))

	const folders = validFolderNames
		.sort((a: string, b: string) => a.localeCompare(b))
		.map((folderName: string) => {
			const folderBlobs = folderBlobsMap.get(folderName) ?? []
			const folderFiles = folderBlobs
				.filter(isVisible)
				.map((blob: IBlobInfo) => mapBlobToRhyBusFile(blob, userNames))
				.sort((a: any, b: any) => a?.Name?.localeCompare(b?.Name))

			const displayName = folderName.startsWith('/') ? folderName.slice(1) : folderName

			return {
				ID: folderName,
				Id: folderName,
				title: displayName,
				is_active: 'true',
				count: folderFiles.length,
				Files: folderFiles,
			}
		})

	return {
		files,
		accordionItems: folders,
		allFilesList: [...files, ...folders.flatMap((folder: any) => folder.Files)],
	}
}

export const uploadDetailFiles = async (itemKey: string, uploadedFiles: File[], folderName?: string | undefined, _me?: any, pageName?: string, existingETagMap?: Map<string, string>): Promise<void> => {
	const success = await uploadFilesToCaseBlobs(itemKey, uploadedFiles, folderName, undefined, pageName, existingETagMap)
	if (!success) throw new Error('One or more files failed to upload')
}

export const deleteDetailFile = async (itemKey: string, file: { Name?: string; eTag?: string; folderName?: string; metadata?: any }, pageName?: string): Promise<void> => {
	await softDeleteBlobFiles(itemKey, [file], pageName)
}

export const getDetailFileContent = async (itemKey: string, file: { Name?: string; folderName?: string }, pageName?: string): Promise<ArrayBuffer> => {
	if (!file?.Name) return new ArrayBuffer(0)
	const blobStore = await getBlobStore(pageName)
	return await blobStore.getContent(itemKey, file.Name, toBlobFolderPath(file.folderName))
}

export const uploadFilesToCaseBlobs = async (itemKey: string, uploadedFiles: File[], folderName?: string, source?: string, pageName?: string, existingETagMap?: Map<string, string>): Promise<boolean> => {
	if (!itemKey || !uploadedFiles?.length) return false

	const blobStore = await getBlobStore(pageName)
	const blobFolderPath = toBlobFolderPath(folderName)
	// source (when provided) is a fixed form identifier used as blobContainerName in metadata
	// so form uploads can be filtered apart from detail-view uploads at retrieval time.
	const blobContainerName = source ?? (pageName ? `${pageName}/${itemKey}` : '')
	const formType = pageName === 'training-detail-view' ? 'training' : 'case'

	// Sequential upload — parallel writes to the same blob item cause ConcurrencyError
	const results: boolean[] = []
	for (const file of uploadedFiles) {
		const currentFile = file.name
		try {
			const metadata = [
				{ Key: 'name', Value: file.name },
				{ Key: 'size', Value: String(file.size) },
				{ Key: 'type', Value: file.type },
				{ Key: 'blobContainerName', Value: blobContainerName },
				{ Key: 'pageName', Value: source ?? pageName ?? '' },
				{ Key: 'IsDeleted', Value: 'false' },
			]
			// Prefer the eTag passed in from component state (avoids a round-trip to getInfo
			// and is more reliable — getInfo can fail when a blob exists, causing the upload
			// to send etag='' which the server rejects with "Unexpected error" on overwrite).
			let uploadETag = EMPTY_BLOB_ETAG
			if (existingETagMap?.has(file.name)) {
				uploadETag = existingETagMap.get(file.name) ?? EMPTY_BLOB_ETAG
			} else {
				try {
					const existing = await blobStore.getInfo(itemKey, file.name, blobFolderPath)
					if (existing) uploadETag = (existing as any).eTag ?? EMPTY_BLOB_ETAG
				} catch {
					// Blob does not exist yet — new upload, keep empty eTag
				}
			}
			const form = new FormData()
			form.append('itemKey', itemKey)
			form.append('metadata', JSON.stringify(metadata))
			form.append('blobName', file.name)
			form.append('blobFolder', blobFolderPath)
			form.append('etag', uploadETag)
			if (blobContainerName) form.append('blobContainerName', blobContainerName)
			form.append('file', file) // file must be last per RhyBus API spec
			console.log({ formType, caseGUID: itemKey, itemKey, attachments: [currentFile], pageName, blobContainerName, uploadStarted: true, uploadCompleted: false, uploadError: null })
			await blobStore.addOrUpdateByFormData(form)
			console.log({ formType, caseGUID: itemKey, itemKey, attachments: [currentFile], pageName, blobContainerName, uploadStarted: true, uploadCompleted: true, uploadError: null })
			results.push(true)
		} catch (err) {
			console.error(`[uploadFilesToCaseBlobs] Failed to upload "${file.name}":`, err)
			console.log({ formType, caseGUID: itemKey, itemKey, attachments: [currentFile], pageName, blobContainerName, uploadStarted: true, uploadCompleted: false, uploadError: String(err) })
			results.push(false)
		}
	}

	return results.every(Boolean)
}

export const createFolderInCaseBlobs = async (itemKey: string, folderName: string, pageName?: string): Promise<boolean> => {
	if (!itemKey || !folderName?.trim()) return false

	const blobStore = await getBlobStore(pageName)
	const folderPath = toBlobFolderPath(folderName)
	const metadata = JSON.stringify([
		{ Key: 'name', Value: FOLDER_PLACEHOLDER_FILE },
		{ Key: 'size', Value: '0' },
		{ Key: 'type', Value: 'application/octet-stream' },
		{ Key: 'IsDeleted', Value: 'false' },
		{ Key: 'pageName', Value: pageName ?? '' },
		{ Key: 'isFolderPlaceholder', Value: 'true' },
	])
	const form = new FormData()
	form.append('itemKey', itemKey)
	form.append('metadata', metadata)
	form.append('blobName', FOLDER_PLACEHOLDER_FILE)
	form.append('blobFolder', folderPath)
	form.append('etag', EMPTY_BLOB_ETAG)
	form.append('file', new Blob([''], { type: 'application/octet-stream' }), FOLDER_PLACEHOLDER_FILE)
	try {
		await blobStore.addOrUpdateByFormData(form)
	} catch (err) {
		// The AWS backend may return an error-shaped response body even when the folder
		// was successfully created. Verify by checking listFolders before surfacing the error.
		const existingFolders = await blobStore.listFolders(itemKey).catch(() => [] as string[])
		const normalizedName = folderName.trim().toLowerCase()
		const folderCreated = (existingFolders ?? []).some((f: string) => {
			const name = f.startsWith('/') ? f.slice(1) : f
			return name.toLowerCase() === normalizedName
		})
		if (!folderCreated) throw err
	}
	return true
}

export const deleteFilesFromCaseBlobs = async (
	itemKey: string,
	selectedFiles: Array<{ Name?: string; eTag?: string; folderName?: string }>,
	folderName?: string,
	pageName?: string
): Promise<boolean> => {
	if (!itemKey || !selectedFiles?.length) return false

	const blobStore = await getBlobStore(pageName)

	await Promise.all(selectedFiles.map(async (file) => {
		if (!file?.Name) return
		const targetFolderPath = folderName !== undefined
			? toBlobFolderPath(folderName)
			: toBlobFolderPath(file.folderName)
		const info = file.eTag
			? { eTag: file.eTag }
			: await blobStore.getInfo(itemKey, file.Name, targetFolderPath)
		await blobStore.delete(itemKey, file.Name, targetFolderPath, info.eTag ?? EMPTY_BLOB_ETAG)
	}))

	return true
}

// Marks blobs as soft-deleted by setting IsDeleted = 'true' in their metadata via addOrUpdate.
// The original file content is preserved in blob storage; structureCaseBlobsForRhyBusFiles
// filters out soft-deleted blobs so they are invisible in all UI listings.
export const softDeleteBlobFiles = async (
	itemKey: string,
	filesToDelete: Array<{ Name?: string; eTag?: string; folderName?: string; metadata?: any }>,
	pageName?: string
): Promise<boolean> => {
	if (!itemKey || !filesToDelete?.length) return false
	const blobStore = await getBlobStore(pageName)

	await Promise.all(
		filesToDelete.map(async (file) => {
			const fileName = file.Name ?? (file as any).name
			if (!fileName) return
			const folderPath = toBlobFolderPath(file.folderName)
			const info = file.eTag
				? { eTag: file.eTag }
				: await blobStore.getInfo(itemKey, fileName, folderPath)
			const existing = file.metadata ?? {}
			const updatedMetadata = new Map<string, string>(
				Object.entries({ ...existing, IsDeleted: 'true' }).map(([k, v]) => [k, String(v)])
			)
			await blobStore.addOrUpdate(
				itemKey,
				fileName,
				folderPath,
				(info as any).eTag ?? EMPTY_BLOB_ETAG,
				'',
				updatedMetadata
			)
		})
	)
	return true
}

export const deleteFolderFromCaseBlobs = async (itemKey: string, folderName: string, pageName?: string): Promise<boolean> => {
	if (!itemKey || !folderName?.trim()) return false

	const blobFolderPath = toBlobFolderPath(folderName)
	const blobStore = await getBlobStore(pageName)
	const folderBlobs = await blobStore.list(itemKey, blobFolderPath)
	await Promise.all((folderBlobs ?? []).map((blob: IBlobInfo) => (
		blobStore.delete(itemKey, blob.fileName, blobFolderPath, blob.eTag ?? EMPTY_BLOB_ETAG)
	)))
	return true
}

export const renameFolderInCaseBlobs = async (itemKey: string, oldName: string, newName: string): Promise<boolean> => {
	const oldTrimmed = normalizeBlobFolder(oldName)
	const newTrimmed = normalizeBlobFolder(newName)
	if (!itemKey || !oldTrimmed || !newTrimmed || oldTrimmed === newTrimmed) return false

	const oldFolderPath = toBlobFolderPath(oldTrimmed)
	const newFolderPath = toBlobFolderPath(newTrimmed)
	const blobStore = await getCaseMasterBlobStore()
	const folderBlobs = await blobStore.list(itemKey, oldFolderPath)
	await Promise.all((folderBlobs ?? []).map((blob: IBlobInfo) => (
		blobStore.blobMove(
			itemKey,
			blob.fileName,
			oldFolderPath,
			blob.eTag ?? EMPTY_BLOB_ETAG,
			blob.fileName,
			newFolderPath,
			blob.metadata
		)
	)))
	return true
}

export const downloadCaseBlob = async (itemKey: string, file: { Name?: string; folderName?: string }, folderName?: string): Promise<void> => {
	if (!itemKey || !file?.Name) return

	const blobStore = await getCaseMasterBlobStore()
	const content = await blobStore.getContent(itemKey, file.Name, folderName !== undefined ? toBlobFolderPath(folderName) : toBlobFolderPath(file.folderName))
	const blob = new Blob([content])
	const blobUrl = window.URL.createObjectURL(blob)
	const link = document.createElement('a')
	link.href = blobUrl
	link.download = file.Name
	document.body.appendChild(link)
	link.click()
	link.remove()
	window.URL.revokeObjectURL(blobUrl)
}

/**
 * Structure files and folders for RhyBusFiles component
 * Groups root files and creates accordion structure for folders
 */
export const structureFilesForRhyBusFiles = (fileItems: any[]) => {
	const files: any[] = []
	const accordionItems: any[] = []
	const allFilesList: any[] = []

	if (!Array.isArray(fileItems)) {
		return { files, accordionItems, allFilesList }
	}

	fileItems.forEach((item: any) => {
		// If item has Files (it's a folder)
		if (item?.Files && item?.Name !== 'Forms') {
			const folderFiles = item?.Files?.results ? item?.Files?.results : item?.Files
			accordionItems.push({
				ID: item?.UniqueId,
				Id: item?.UniqueId,
				title: item?.Name,
				is_active: 'true',
				count: folderFiles?.length || 0,
				Files: folderFiles || []
			})
			// Add all files from folder to all files list
			folderFiles?.forEach((f: any) => {
				allFilesList.push(f)
			})
		} else {
			// Root level file
			if (item?.Name !== 'Forms') {
				files.push(item)
				allFilesList.push(item)
			}
		}
	})

	// Sort files and folders alphabetically
	files.sort((a: any, b: any) => a?.Name?.localeCompare(b?.Name))
	accordionItems.sort((a: any, b: any) => a?.title?.localeCompare(b?.title))

	return {
		files,
		accordionItems,
		allFilesList
	}
}

/**
 * Create initial file structure with empty state
 */
export const createEmptyFileStructure = () => ({
	files: [],
	accordionItems: [],
	allFilesList: []
})

/**
 * Fetch files from SharePoint and structure for RhyBusFiles component
 * Replaces static initialFiles with real dynamic data
 */
export const fetchAndStructureFilesForRhyBusFiles = async (itemGuid: string, pageName?: string) => {
	try {
		return await structureCaseBlobsForRhyBusFiles(itemGuid, pageName)
		// Import here to avoid circular dependency
		const { fetchFilesbasedOnGUID } = await import('../pages/detailview/detail-view')
		// Fetch real files from SharePoint
		const fileItems = await fetchFilesbasedOnGUID(itemGuid)
		if (!fileItems || !Array.isArray(fileItems)) {
			return createEmptyFileStructure()
		}

		// Structure files for RhyBusFiles component
		const structure = structureFilesForRhyBusFiles(fileItems)
		return structure
	} catch (error) {
		console.error('Error fetching case files:', error)
		return createEmptyFileStructure()
	}
}
export const loadBlobFilesForItem = async (eKey: string, pageName: string, containerName: string): Promise<any[]> => {
	try {
		const fileStructure = await getFilesForDetail(eKey, pageName)
		return (fileStructure?.allFilesList ?? [])
			.filter((f: any) => f.metadata?.blobContainerName === containerName)
			.map((f: any) => ({
				...f,
				FileName: f.metadata?.name || f.Name || '',
				name: f.metadata?.name || f.Name || '',
			}))
	} catch {
		return []
	}
}

export const formatPhoneNumber = (phoneNumber: any) => {
	const cleanedNumber = phoneNumber?.replace(/\D/g, '')
	const match = cleanedNumber?.match(/(\d{0,3})(\d{0,3})(\d{0,4})/)

	if (!match?.[2]) {
		return match ? match[1] : ''
	}

	const formattedNumber = `(${match[1]})${match[2]}${match[3] ? `-${match[3]}` : ''}`
	return formattedNumber
}
/**
 * Get files - returns real data if itemGuid provided, otherwise returns initial mock data
 */
export const getFilesForDetail = async (itemGuid?: string, pageName?: string) => {
	if (itemGuid) {
		return await fetchAndStructureFilesForRhyBusFiles(itemGuid, pageName)
	}
	return createEmptyFileStructure()
}

// ============================================
// MASTER ITEM
// ============================================

export const sendApproveNotification = async (itemGuid: string, requestId?: string): Promise<void> => {
	if (!await checkNotifPref('resolveInquiry')) return
	await sendNotification({
		title: `Request ${itemGuid} - has been Completed`,
		activity: 'Approve',
		subject: `Request ${requestId || itemGuid} - has been Completed`,
		notificationType: 'Action',
		status: 'Approve',
		itemGuid,
	})
}

export const sendFileNotification = async (
	itemGuid: string,
	action: 'Add Document' | 'Delete Document',
	requestId?: string
): Promise<void> => {
	const prefField = action === 'Add Document' ? 'documentAdd' : 'documentDelete'
	if (!await checkNotifPref(prefField)) return
	const subject = action === 'Add Document'
		? `Request ${itemGuid} - Updates to Files`
		: `Request ${itemGuid} - Files Deleted`
	await sendNotification({
		title: subject,
		activity: action,
		subject: action === 'Add Document' ? `Request ${requestId || itemGuid} - Updates to Files` : `Request ${requestId || itemGuid} - Files Deleted`,
		notificationType: 'Notification',
		status: action,
		itemGuid,
	})
}

export const sendCancelNotification = async (itemGuid: string, requestId?: string): Promise<void> => {
	if (!await checkNotifPref('cancelInquiry')) return
	await sendNotification({
		title: `Request ${itemGuid} - has been Cancelled`,
		activity: 'Cancel',
		subject: `Request ${requestId || itemGuid} - has been Cancelled`,
		notificationType: 'Action',
		status: 'Cancel',
		itemGuid,
	})
}

export const buildSectionDFields = (item: any): Array<{ label: string; value: string }> => {
	if (!item) return []
	const formatDate = (date: string) =>
		date ? new Date(date).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' }) : '-'
	return [
		{ label: 'Acquisition Method', value: item?.AcquisitionMethod || '-' },
		{ label: 'UFMS Target Date', value: formatDate(item?.UFMSTargetDate) },
	]
}

export const fetchRequisitionNumbersByGUID = async (itemGuid: string): Promise<string[]> => {
	const list = await rhybusRListsHttp().getList(ListNames.REQUISITION_NUMBER)
	try {
		const items = [...(await list.query().filter(`ItemGUID eq '${itemGuid}'`).get<any>())]
		return (items ?? []).map((item: any) => item?.RequisitionNumber ?? '').filter(Boolean)
	} catch (error) {
		console.error('Error fetching requisition numbers:', error)
		return []
	}
}

export const fetchContractDurationByGUID = async (itemGuid: string): Promise<any[]> => {
	const list = await rhybusRListsHttp().getList(ListNames.CONTRACT_DURATION)
	try {
		const items = [...(await list.query().filter(`ItemGUID eq '${itemGuid}'`).get<any>())]
		return items ?? []
	} catch (error) {
		console.error('Error fetching contract duration:', error)
		return []
	}
}

export const buildContractDurationFields = (items: any[]): Array<{ id: number; baseYearStartDate: string; baseYearEndDate: string; baseYearCost: string }> => {
	if (!items?.length) return []
	const formatDate = (date: string) =>
		date ? new Date(date).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' }) : '-'
	return items.map((item: any) => ({
		id: item?.ID ?? 0,
		baseYearStartDate: formatDate(item?.BaseYearStartDate),
		baseYearEndDate: formatDate(item?.BaseYearEndDate),
		baseYearCost: item?.BaseYearCost || '-',
	}))
}

export const fetchMasterItemByGUID = async (itemGuid: string): Promise<any | null> => {
	const list = await rhybusRListsHttp().getList(ListNames.MASTERLIST)
	try {
		const items = [...(await list.query().filter(`ItemGUID eq '${itemGuid}'`).get<any>())]
		return items?.[0] ?? null
	} catch (error) {
		console.error('Error fetching master item:', error)
		return null
	}
}

export const fetchHistoryByGUID = async (itemGuid: string): Promise<I_History[]> => {
	const list = await rhybusRListsHttp().getList(ListNames.HISTORYLIST)
	try {
		const items = [...(await list.query().filter(`ItemGUID eq '${itemGuid}'`).get<any>())] as I_History[]
		return items ?? []
	} catch (error) {
		console.error('Error fetching history:', error)
		return []
	}
}

const formatDetailDate = (date: string) =>
	date ? new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '-'

export const buildSectionBFields = (item: any): Array<{ label: string; value: string }> => {
	if (!item) return []
	return [
		{ label: 'Requestor Phone Number', value: formatPhoneNumber(item?.RequestorPhoneNumber) || '-' },
		{ label: 'POC Name', value: item?.POCName || '-' },
		{ label: 'POC Phone Number', value: formatPhoneNumber(item?.POCPhoneNumber) || '-' },
		{ label: 'Other Name', value: item?.OtherName || '-' },
		{ label: 'Other Role', value: item?.OtherRole || '-' },
		{ label: 'Other Phone Number', value: formatPhoneNumber(item?.OtherPhoneNumber) || '-' },
	]
}

export const buildSectionBCTRFields = (item: any) => {
	if (!item) return { requiresCTRSupport: '-', supportingVendor: '-', describeThatSupport: '-' }
	return {
		requiresCTRSupport: item?.RequiresCTRSupport ? 'Yes' : 'No',
		supportingVendor: item?.SupportingCTRVendor || '-',
		describeThatSupport: item?.DescribeThatSupport || '-',
	}
}

export const buildSectionAFields = (item: any): Array<{ label: string; value: string }> => {
	if (!item) return []
	return [
		{ label: 'Division', value: item?.Division || '-' },
		{ label: 'Need by Date', value: item?.NeedByDate || '-' },
		{ label: 'Estimated Current Year Cost', value: item?.EstimatedCurrentYearCost ? formatCurrency(item.EstimatedCurrentYearCost) : '-' },
	]
}

export const buildSectionCFields = (item: any): Array<{ label: string; value: string }> => {
	if (!item) return []
	return [
		{ label: 'Requirement Description', value: item?.RequirementDescription || '-' },
		{ label: 'Impact Statement', value: item?.ImpactStatement || '-' },
		{ label: 'Level of Urgency', value: item?.LevelOfUrgency || '-' },
		{ label: 'Scope of Work', value: item?.ScopeOfWork || '-' },
		{ label: 'Other Branches Who Leverage the Tool', value: item?.Branch || '-' },
	]
}

export const buildSectionCSoleSource = (item: any) => {
	if (!item) return { soleSourceRequired: '-', requestedVendor: '-', describeWhySoleSource: '-' }
	return {
		soleSourceRequired: item?.SoleSourceRequired || '-',
		requestedVendor: item?.RequestedVendor || '-',
		describeWhySoleSource: item?.DescribeWhySoleSource || '-',
	}
}

export const buildSectionCBrandName = (item: any) => {
	if (!item) return { brandNameJustifications: '-', describeWhy: '-' }
	return {
		brandNameJustifications: item?.BrandNameJustifications || '-',
		describeWhy: item?.DescribeWhy || '-',
	}
}

export const buildSectionEFields = (item: any): Array<{ label: string; value: string }> => {
	if (!item) return []
	return [
		{ label: 'Budget Reference', value: item?.BudgetReference || '-' },
		{ label: 'Investment', value: item?.Investment || '-' },
		{ label: 'Contract Specialist', value: item?.ContractSpecialist || '-' },
		{ label: 'Awarded Vendor', value: item?.AwardedVendor || '-' },
		{ label: 'Contract Start Date', value: formatDetailDate(item?.ContractStartDate) || '-' },
		{ label: 'Contract End Date', value: formatDetailDate(item?.ContractEndDate) || '-' },
		{ label: 'HHS Award Number', value: item?.HHSAwardNumber || '-' },
		{ label: 'Object Class', value: item?.ObjectClass || '-' },
		{ label: 'Master Object Class', value: item?.MasterObjectClass || '-' },
	]
}

export const buildDetailFields = (item: any): Array<{ label: string; value: string }> => {
	if (!item) return []
	return [
		{ label: 'Requirement Type', value: item?.RequirementTitle || '-' },
		{ label: 'Priority', value: item?.LevelOfUrgency || '-' },
		{ label: 'Fiscal Year', value: item?.FY || '-' },
		{ label: 'Status', value: item?.StatusTitle || '-' },
		{ label: 'Submitted By', value: item?.ItemCreatedBy?.Title || '-' },
		{ label: 'Last Updated', value: formatDetailDate(item?.ItemModified) || '-' },
	]
}

export const buildSummaryHeaderFields = (item: any): Array<{ label: string; value: string }> => {
	if (!item) return []
	return [
		{ label: 'Product', value: item?.Product || '-' },
		{ label: 'TBM', value: item?.TBM || '-' },
		{ label: 'Branch', value: item?.Branch || '-' },
		{ label: 'COR', value: item?.COR?.Title || '-' },
		{ label: 'Requested Amount', value: formatCurrency(item?.RequestedAmount ?? '') },
		{ label: 'Revised Amount', value: formatCurrency(item?.RevisedAmount ?? '') },
		{ label: 'Funded Amount', value: formatCurrency(item?.FundedAmount ?? '') },
		{ label: 'Unfunded Amount', value: formatCurrency(item?.UnfundedAmount ?? '') },
		{ label: 'Submitted Date', value: formatDetailDate(item?.ItemCreated) || '-' },
	]
}

// ============================================
// TEAM MEMBER MANAGEMENT
// ============================================

export const fetchTeamMembersByGUID = async (itemGuid: any) => {
	const list = await rhybusRListsHttp().getList(ListNames.TEAMMEMBERS)
	try {
		const items = [...(await list.query().filter(`CaseGUID eq '${itemGuid}'`).get<any>())]
		const teamItem = items.find((item: any) => item.CaseGUID === itemGuid)

		if (!teamItem) {
			return null
		}

		const teamMemberIds = Array.isArray(teamItem.TeamMembers)
			? teamItem.TeamMembers
			: Array.isArray(teamItem.TeamMembersId)
				? teamItem.TeamMembersId
				: []

		const teamMembers = await Promise.all(
			teamMemberIds
				.filter((userId: any) => userId !== null && userId !== undefined && String(userId).trim())
				.map(async (userId: any) => {
					try {
						const user = await getUserByUserId(String(userId))
						const resolvedUser = user as any
						return {
							ID: resolvedUser.userId,
							Id: resolvedUser.userId,
							userId: resolvedUser.userId,
							Title: resolvedUser.persona || resolvedUser.email || String(userId),
							Email: resolvedUser.email || '',
							EMail: resolvedUser.email || '',
							LoginName: resolvedUser.userId,
						}
					} catch (userError) {
						console.error(`Error fetching team member ${userId}:`, userError)
						return null
					}
				})
		)

		return {
			...teamItem,
			TeamMembers: teamMembers.filter(Boolean),
		}
	} catch (error) {
		console.error('Error fetching team members:', error)
		return null
	}
}
const normalizeTeamMemberId = (value: any): string => {
	if (value === null || value === undefined) {
		return ''
	}
	return String(value).trim()
}
export const addTeamMember = async (
	currentTeamMembers: any[],
	currentTeamMembersListID: number | null,
	itemGuid: string,
	userToAdd: any,
	requirementId?: string,
	meval?: any,
	ekey?: string,
	eTag?: string
): Promise<boolean> => {
	try {
		const resolvedUserId = typeof userToAdd === 'object' ? userToAdd?.Id ?? userToAdd : userToAdd
		const newUserId = normalizeTeamMemberId(resolvedUserId)

		if (!newUserId) {
			console.error('Could not resolve user ID from:', userToAdd)
			return false
		}
		const allUserIds = [...new Set(currentTeamMembers.map((user: any) => normalizeTeamMemberId(user.ID ?? user.Id ?? user.userId ?? user.LoginName)).filter(Boolean).concat(newUserId))]
		const objectArray: any = {
			ItemModified: new Date().toISOString(),
			ItemModifiedBy: meval?.userId,
			TeamMembers: allUserIds
		}
		if (!currentTeamMembersListID) {
			objectArray.ItemCreated = new Date().toISOString()
			objectArray.ItemCreatedBy = meval?.userId
			objectArray.CaseGUID = itemGuid
			await (await rhybusRListsHttp().getList(ListNames.TEAMMEMBERS)).addOrUpdateItem(objectArray)
		}
		else {
			await (await rhybusRListsHttp().getList(ListNames.TEAMMEMBERS)).addOrUpdateItem({ eKey: ekey, eTag: eTag, ...objectArray })
		}
		return true
	} catch (error) {
		console.error('Error adding/updating team member:', error)
		return false
	}
}

export const deleteTeamMember = async (
	currentTeamMembers: any[],
	selectedMembers: any[],
	currentTeamMembersListID: number | null,
	itemGuid: string,
	meval?: any,
	ekey?: string,
	eTag?: string
): Promise<boolean> => {
	try {
		const selectedIds = new Set(
			selectedMembers.map((m: any) => normalizeTeamMemberId(m.ID ?? m.Id ?? m.userId ?? m.LoginName)).filter(Boolean)
		)
		const remainingIds = currentTeamMembers
			.map((u: any) => normalizeTeamMemberId(u.ID ?? u.Id ?? u.userId ?? u.LoginName))
			.filter((id) => id && !selectedIds.has(id))
		const unique = [...new Set(remainingIds)]
		const objectArray: any = {
			ItemModified: new Date().toISOString(),
			ItemModifiedBy: meval?.userId,
			TeamMembers: unique,
		}
		if (!currentTeamMembersListID) {
			objectArray.ItemCreated = new Date().toISOString()
			objectArray.ItemCreatedBy = meval?.userId
			objectArray.CaseGUID = itemGuid
			await (await rhybusRListsHttp().getList(ListNames.TEAMMEMBERS)).addOrUpdateItem(objectArray)
		} else {
			await (await rhybusRListsHttp().getList(ListNames.TEAMMEMBERS)).addOrUpdateItem({ eKey: ekey, eTag: eTag, ...objectArray })
		}
		return true
	} catch (error) {
		console.error('Error deleting team member(s):', error)
		return false
	}
}

export const addTeamMemberToSharePoint = async (
	currentTeamMembers: any[],
	currentTeamMembersListID: number | null,
	itemGuid: string,
	userToAdd: any,
	requestId?: string
): Promise<boolean> => {
	const newUserId = typeof userToAdd === 'object' && userToAdd?.Id
		? Number(userToAdd.Id)
		: Number(userToAdd)

	if (!newUserId || isNaN(newUserId)) {
		console.error('Could not resolve user ID from:', userToAdd)
		return false
	}

	const allUserIds: number[] = currentTeamMembers.map((user: any) => Number(user.ID))
	allUserIds.push(newUserId)

	const objectArray: any = {
		ItemModified: new Date(),
		ItemModifiedById: 0, // User context handled by RhyBus API
	}

	if (!currentTeamMembersListID) {
		objectArray.ItemCreated = new Date()
		objectArray.ItemCreatedById = 0
		objectArray.ItemGUID = itemGuid
		objectArray.TeamMembersId = allUserIds
		await (await rhybusRListsHttp().getList(ListNames.TEAMMEMBERS)).addOrUpdateItem(objectArray)
	} else {
		objectArray.TeamMembersId = allUserIds
		await (await rhybusRListsHttp().getList(ListNames.TEAMMEMBERS)).addOrUpdateItem({ eKey: String(currentTeamMembersListID), ...objectArray })
	}

	if (await checkNotifPref('teamMemberAdd')) {
		await sendNotification({
			title: `Request ${itemGuid} - Team Member has been added`,
			activity: 'Add Team Member',
			subject: `Request ${requestId || itemGuid} - Team Member has been added`,
			notificationType: 'Notification',
			status: 'Add Team Member',
			itemGuid,
		})
	}

	return true
}

export const deleteTeamMembersFromSharePoint = async (
	currentTeamMembersListID: number | null,
	currentTeamMembers: any[],
	selectedMembers: any[],
	itemGuid?: string,
	requestId?: string
): Promise<boolean> => {
	const uniqueSelected = [...new Set(selectedMembers)]
	const remainingIds: number[] = currentTeamMembers
		.filter(({ ID }: any) => !uniqueSelected.map((x: any) => x.ID).includes(ID))
		.map((x: any) => Number(x.ID))

	await (await rhybusRListsHttp().getList(ListNames.TEAMMEMBERS)).addOrUpdateItem({
		eKey: String(currentTeamMembersListID),
		TeamMembersId: remainingIds,
		ItemModified: new Date(),
		ItemModifiedById: 0, // User context handled by RhyBus API
	})

	if (itemGuid && await checkNotifPref('teamMemberDelete')) {
		await sendNotification({
			title: `Request ${itemGuid} - Team Member has been deleted`,
			activity: 'Delete Team Member',
			subject: `Request ${requestId || itemGuid} - Team Member has been deleted`,
			notificationType: 'Notification',
			status: 'Delete Team Member',
			itemGuid,
		})
	}
	return true
}

export const getTeamMemberPeopleSuggestions = async (value: string): Promise<any[]> => {
	try {
		const matches = [...(await rhybusWhoHttp().Picker(value))]
		return matches.map((result: any) => {
			const display = result.persona ?? result.displayName ?? result.cn ?? result.profileName ?? result.email ?? ''
			const userKey = result.rid ?? result.userId ?? result.edipi ?? result.upn ?? result.email ?? ''
			return {
				DisplayText: display,
				email: result.email ?? '',
				Key: userKey,
				rid: userKey,
				persona: display,
			}
		})
	} catch (error) {
		console.error('Error fetching people picker suggestions:', error)
		return []
	}

}

export const validateTeamMemberForSP = (
	currentTeamMembers: any[],
	userData: any
): { isValid: boolean; error: string } => {
	if (typeof userData === 'string' || !userData?.Id) {
		return { isValid: true, error: '' }
	}

	// User validation - this check may need to be implemented via RhyBus API
	// const currentUserId = await getCurrentUserId()
	// if (userData.Id === currentUserId) {
	// 	return { isValid: false, error: 'Submitter cannot be added as a team member' }
	// }

	const alreadyExists = currentTeamMembers.some((member: any) => userData.Id === member?.ID)
	if (alreadyExists) {
		return { isValid: false, error: 'User is already a team member' }
	}

	return { isValid: true, error: '' }
}

export const resolveTeamMemberUser = (
	event: any,
	setUserToAdd: (user: any) => void
): void => {
	if (!event) return

	let email = ''
	let userKey = ''

	if (typeof event === 'string') {
		email = event
	} else {
		email = event?.EntityData?.Email ?? event?.email ?? event?.EMail ?? event?.Mail ?? ''
		userKey = event?.EntityData?.UserKey ?? event?.UserKey ?? event?.Key ?? event?.rid ?? ''
		if (!email && typeof event?.DisplayText === 'string' && event.DisplayText.includes('@')) {
			email = event.DisplayText
		}
	}

	if (email) {
		rhybusWhoHttp().Picker(email)
			.then(async (matches: any) => {
				const matchArr = [...matches]
				const userData = matchArr[0]
				const id = userData?.rid ?? userData?.userId ?? userKey
				setUserToAdd({
					Id: id,
					LoginName: userKey || userData?.rid || userData?.userId || email,
					Email: email,
					EMail: email,
					Title: userData?.persona ?? userData?.displayName ?? email,
				})
			})
			.catch((e: any) => {
				setUserToAdd('')
				console.error(e)
			})
	}
}

// ============================================
// DISCUSSION MANAGEMENT
// ============================================

const normalizeDiscussionItem = async (item: any): Promise<any> => {
	const resolvedId = item?.eKey ?? ''
	let title = typeof item?.ItemCreatedBy === 'string' ? item.ItemCreatedBy.trim() : '-'
	if (item?.ItemCreatedBy) {
		try {
			const user = await getUserByUserId(String(item.ItemCreatedBy))
			if ((user as any)?.persona) title = (user as any).persona
		} catch {
			// keep raw string as fallback
		}
	}
	return {
		...item,
		Id: resolvedId,
		ItemCreatedBy: { Title: title },
	}
}

export const fetchDiscussionsByGUID = async (itemGuid: string | undefined): Promise<any[]> => {
	const list = await rhybusRListsHttp().getList(ListNames.DISCUSSION)
	try {
		const rawItems = [...(await list.query().filter(`ItemGUID eq '${itemGuid}'`).orderBy([{ orderBy: 'ItemCreated', ascending: false }]).get<any>())]
		if (!Array.isArray(rawItems)) return []
		return await Promise.all(rawItems.map(normalizeDiscussionItem))
	} catch (error) {
		console.error('Error fetching discussions:', error)
		return []
	}
}


export const addDiscussionToSharePoint = async (
	itemGuid: string | undefined,
	discussionData: any,
	meval?: any,
): Promise<boolean> => {
	const objectArray: any = {
		Subject: discussionData.Subject || null,
		Comment: discussionData.Comment || null,
		CommentType: discussionData.CommentType || 'Public',
		IsActionComment: false,
		DocumentName: discussionData.DocumentName || '',
		ItemGUID: discussionData.formType === 'Training'
			? discussionData.TrainingTrackGUID
			: (discussionData.CaseGUID || itemGuid),
		ItemID: discussionData.formType === 'Training'
			? discussionData.TrainingTrackGUID
			: (discussionData.CaseGUID || itemGuid),
		CommentTo: discussionData.CommentTo || null,
		ItemModified: new Date().toISOString(),
		ItemModifiedBy: meval?.userId || '',
		ItemCreated: new Date().toISOString(),
		ItemCreatedBy: meval?.userId || '',
	}
	try {
		await (await rhybusRListsHttp().getList(ListNames.DISCUSSION)).addOrUpdateItem(objectArray)
		return true
	} catch (error) {
		console.error('Error adding discussion:', error)
		return false
	}
}

export const addStatusDiscussionToSharePoint = async (
	itemGuid: string,
	subject: string,
	userRole: string,
	meval?: any,
	comment = '',
): Promise<boolean> => {
	const now = new Date().toISOString()
	const objectArray: any = {
		ItemGUID: itemGuid,
		ItemID: itemGuid,
		Subject: subject,
		Comment: comment,
		IsActionComment: true,
		ParentCommentID: null,
		UserRole: userRole,
		// CommentType: 'Status',
		CommentTo: null,
		DocumentName: '',
		ItemCreated: now,
		ItemCreatedBy: meval?.userId || '',
		ItemModified: now,
		ItemModifiedBy: meval?.userId || '',
	}
	try {
		await (await rhybusRListsHttp().getList(ListNames.DISCUSSION)).addOrUpdateItem(objectArray)
		return true
	} catch (error) {
		console.error('Error adding status discussion:', error)
		return false
	}
}

const STATUS_PROGRESSION: Record<number, number> = {
	2: 4, // SUBMITTED → WaitingforInternalReview
	4: 5, // WaitingforInternalReview → WaitingforDSCAFunding
	5: 6, // WaitingforDSCAFunding → Implemented
	6: 7, // Implemented → Active
	7: 8, // Active → Closed
}

const TRAINING_STATUS_PROGRESSION: Record<number, number> = {
	2: 4, // SUBMITTED → seatValidation
	4: 5, // seatValidation → execution
	5: 6, // execution → utilization
	6: 7, // utilization → Billing
	7: 8, // Billing → Reconciliation
	8: 9, // Reconciliation → closure
}

const getNextStatusCode = (current: number | null): number | null => {
	if (current === null) return null
	return STATUS_PROGRESSION[current] ?? null
}

const getNextTrainingStatusCode = (current: number | null): number | null => {
	if (current === null) return null
	return TRAINING_STATUS_PROGRESSION[current] ?? null
}

export const generateTrainingItem = async (
	trainingTrackGUID: string,
	trainingTrackId: string,
	comment: string,
	currentStatusCode: number | null,
	userRole: string,
	meval?: any,
): Promise<boolean> => {
	const now = new Date().toISOString()
	const userId = meval?.profileName || ''
	try {
		const nextStatusCode = getNextTrainingStatusCode(currentStatusCode)
		if (nextStatusCode === null) throw new Error('No next status available for current training status')

		const trainingList = await rhybusRListsHttp().getList(ListNames.DSCA_TRAINING_TRACK)
		const allItems = [...(await trainingList.query().get<any>())]
		const rawItem = allItems.find((i: any) => i.TrainingTrackGUID === trainingTrackGUID)
		if (!rawItem?.eKey) throw new Error(`Training item not found for TrainingTrackGUID: ${trainingTrackGUID}`)

		await trainingList.addOrUpdateItem({
			eKey: rawItem.eKey,
			eTag: rawItem.eTag,
			StatusCode: nextStatusCode,
			ItemModified: now,
			ItemModifiedBy: userId,
		} as any)

		const historyList = await rhybusRListsHttp().getList(ListNames.DSCA_TRAINING_HISTORY)
		await historyList.addOrUpdateItem({
			TrainingTrackGUID: trainingTrackGUID,
			TrainingTrackID: trainingTrackId,
			StatusCode: nextStatusCode,
			PreviousStatusCode: currentStatusCode,
			Action: 'GenerateFunding',
			UserRole: userRole,
			ItemCreated: now,
			ItemCreatedBy: userId,
			ItemModified: now,
			ItemModifiedBy: userId,
		} as any)

		await addStatusDiscussionToSharePoint(trainingTrackGUID, 'GenerateFunding', userRole, meval, comment)


		return true
	} catch (err) {
		console.error('[generateTrainingItem]', err)
		return false
	}
}

export const approveCase = async (
	caseGUID: string,
	caseID: string,
	comment: string,
	currentStatusCode: number | null,
	userRole: string,
	meval?: any,
): Promise<boolean> => {
	const now = new Date().toISOString()
	const userId = meval?.profileName || ''
	try {
		const nextStatusCode = getNextStatusCode(currentStatusCode)
		if (nextStatusCode === null) throw new Error('No next status available for current status')

		const caseList = await rhybusRListsHttp().getList(ListNames.DSCA_CASE_MASTER)
		const allItems = [...(await caseList.query().get<any>())]
		const rawItem = allItems.find((i: any) => i.CaseGUID === caseGUID)
		if (!rawItem?.eKey) throw new Error(`Case not found for CaseGUID: ${caseGUID}`)

		await caseList.addOrUpdateItem({
			eKey: rawItem.eKey,
			eTag: rawItem.eTag,
			StatusCode: nextStatusCode,
			ItemModified: now,
			ItemModifiedBy: userId,
		} as any)

		const historyList = await rhybusRListsHttp().getList(ListNames.DSCA_CASE_HISTORY)
		await historyList.addOrUpdateItem({
			CaseGUID: caseGUID,
			CaseID: caseID,
			StatusCode: nextStatusCode,
			PreviousStatusCode: currentStatusCode,
			Action: 'Approve',
			UserRole: userRole,
			ItemCreated: now,
			ItemCreatedBy: userId,
			ItemModified: now,
			ItemModifiedBy: userId,
		} as any)

		await addStatusDiscussionToSharePoint(caseGUID, 'Approved', userRole, meval, comment)

		return true
	} catch (err) {
		console.error('[approveCase]', err)
		return false
	}
}

export const cancelCase = async (
	caseGUID: string,
	caseID: string,
	comment: string,
	currentStatusCode: number | null,
	userRole: string,
	meval?: any,
): Promise<boolean> => {
	const now = new Date().toISOString()
	const userId = meval?.profileName || ''
	try {
		const caseList = await rhybusRListsHttp().getList(ListNames.DSCA_CASE_MASTER)

		const allItems = [...(await caseList.query().get<any>())]
		const rawItem = allItems.find((i: any) => i.CaseGUID === caseGUID)
		if (!rawItem?.eKey) throw new Error(`Case not found for CaseGUID: ${caseGUID}`)

		await caseList.addOrUpdateItem({
			eKey: rawItem.eKey,
			eTag: rawItem.eTag,
			StatusCode: 3,
			ItemModified: now,
			ItemModifiedBy: userId,
		} as any)

		const historyList = await rhybusRListsHttp().getList(ListNames.DSCA_CASE_HISTORY)
		await historyList.addOrUpdateItem({
			CaseGUID: caseGUID,
			CaseID: caseID,
			StatusCode: 3,
			PreviousStatusCode: currentStatusCode,
			Action: 'Cancel',
			UserRole: userRole,
			ItemCreated: now,
			ItemCreatedBy: userId,
			ItemModified: now,
			ItemModifiedBy: userId,
		} as any)

		await addStatusDiscussionToSharePoint(caseGUID, 'Cancelled', userRole, meval, comment)

		return true
	} catch (err) {
		console.error('[cancelCase]', err)
		return false
	}
}

export const cancelTraining = async (
	trainingTrackGUID: string,
	trainingTrackId: string,
	comment: string,
	currentStatusCode: number | null,
	userRole: string,
	meval?: any,
): Promise<boolean> => {
	const now = new Date().toISOString()
	const userId = meval?.profileName || ''
	try {
		const trainingList = await rhybusRListsHttp().getList(ListNames.DSCA_TRAINING_TRACK)
		const allItems = [...(await trainingList.query().get<any>())]
		const rawItem = allItems.find((i: any) => i.TrainingTrackGUID === trainingTrackGUID)
		if (!rawItem?.eKey) throw new Error(`Training item not found for TrainingTrackGUID: ${trainingTrackGUID}`)

		await trainingList.addOrUpdateItem({
			eKey: rawItem.eKey,
			eTag: rawItem.eTag,
			StatusCode: 3,
			ItemModified: now,
			ItemModifiedBy: userId,
		} as any)

		const historyList = await rhybusRListsHttp().getList(ListNames.DSCA_TRAINING_HISTORY)
		await historyList.addOrUpdateItem({
			TrainingTrackGUID: trainingTrackGUID,
			TrainingTrackID: trainingTrackId,
			StatusCode: 3,
			PreviousStatusCode: currentStatusCode,
			Action: 'Cancel',
			UserRole: userRole,
			ItemCreated: now,
			ItemCreatedBy: userId,
			ItemModified: now,
			ItemModifiedBy: userId,
		} as any)

		await addStatusDiscussionToSharePoint(trainingTrackGUID, 'Cancelled', userRole, meval, comment)

		return true
	} catch (err) {
		console.error('[cancelTraining]', err)
		return false
	}
}

export const addReplyToSharePoint = async (
	itemGuid: string | undefined,
	replyData: any,
	meval?: any,
): Promise<boolean> => {
	const objectArray: any = {
		Subject: '',
		ParentCommentID: replyData.ParentCommentID,
		Comment: replyData.Comment,
		IsActionComment: false,
		DocumentName: replyData.DocumentName || '',
		ItemGUID: itemGuid,
		ItemID: itemGuid,
		ItemModified: new Date().toISOString(),
		ItemModifiedBy: meval?.userId || '', // User context handled by RhyBus API
		ItemCreated: new Date().toISOString(),
		ItemCreatedBy: meval?.userId || '',
	}
	try {
		await (await rhybusRListsHttp().getList(ListNames.DISCUSSION)).addOrUpdateItem(objectArray)
		// if (await checkNotifPref('replyDiscussion')) {
		// 	await sendNotification({
		// 		title: `Request ${itemGuid} - Discussion Topic Replied`,
		// 		activity: 'Reply Discussion',
		// 		subject: `Request ${requestId || itemGuid} - Discussion Topic Replied`,
		// 		notificationType: 'Notification',
		// 		status: 'Reply Discussion',
		// 		itemGuid,
		// 	})
		// }
		return true
	} catch (error) {
		console.error('Error adding reply:', error)
		return false
	}
}

export const fetchDiscussionPeopleSuggestions = async (value: string): Promise<any[]> => {
	try {
		const matches = [...(await rhybusWhoHttp().Picker(value))]
		return matches.map((result: any) => ({
			DisplayText: result.displayName,
			email: result.email,
			Key: result.userId,
			rid: result.userId,
			persona: result.displayName,
		}))
	} catch (error) {
		console.error('Error fetching discussion people suggestions:', error)
		return []
	}
}

export const separateDiscussionsAndStatusNotes = (items: any[]) => {
	const discussions = items.filter((item: any) => !item.IsActionComment)
	const statusNotes = items.filter((item: any) => item.IsActionComment)
	return { discussions, statusNotes }
}


export const searchDiscussions = (searchWord: string, allDiscussions: any[]): any[] => {
	if (!searchWord) return allDiscussions

	const matchedSet = new Set<any>()
	const lowerWord = searchWord.toLowerCase()

	allDiscussions.forEach((item: any) => {
		const matches =
			item?.Comment?.toLowerCase()?.includes(lowerWord) ||
			item?.Subject?.toLowerCase()?.includes(lowerWord) ||
			item?.DocumentName?.toLowerCase()?.includes(lowerWord) ||
			item?.ItemCreatedBy?.Title?.toLowerCase()?.includes(lowerWord) ||
			item?.CommentTo?.toLowerCase()?.includes(lowerWord)

		if (matches) {
			matchedSet.add(item)
			// Also include the parent if this is a reply
			if (item.ParentCommentID) {
				const parent = allDiscussions.find(
					(p: any) => p.Id?.toString() === item.ParentCommentID
				)
				if (parent) matchedSet.add(parent)
			}
		}
	})

	return Array.from(matchedSet)
}

export const checklistItems = [
	{ label: 'Operational Requirements/Inventory', isMandatory: true },
	{ label: 'Acquisition Strategy', isMandatory: true },
	{ label: 'Acquisition Plan', isMandatory: true },
	{ label: 'Market Research', isMandatory: true },
	{ label: 'IGCE (Independent Cost Estimate)', isMandatory: true },
	{ label: 'IGCE Supporting Quotes (Or GOVCC Invoice)', isMandatory: true },
	{ label: 'Signature Routing Slip', isMandatory: true },
	{ label: '7600 A', isMandatory: true },
	{ label: '7600 B', isMandatory: true },
	{ label: 'HHS -755 (GOVCC)', isMandatory: true },
	{ label: 'PSC-59', isMandatory: true },
	{ label: 'Other', isMandatory: true },
]

// ============================================
// NOTIFICATION PREFERENCE
// ============================================

export const checkNotifPref = async (field: string): Promise<boolean> => {
	try {
		// User context handled by RhyBus API - loginName not needed for filtering
		const list = await rhybusRListsHttp().getList(ListNames.USERPREFERENCES)
		const items = [...(await list.query().filter(`Source eq 'notification-preferences'`).get<any>())]
		const item = items?.[0]
		if (!item) return true
		const prefs = JSON.parse(item.SelectedColumns ?? '{}')
		return prefs[field] === true
	} catch {
		return true
	}
}
